// src/api/konstructGateway.js
import Gateway from './gateway';

/**
 * Шлюз для работы с конструктором и инженерным доком
 * 
 * Обеспечивает доступ к:
 * - Чертежам кораблей, структур, транспорта
 * - Активным объектам для модернизации
 * - Модулям и их шаблонам
 * - Генерации чертежей (авто, полуавто, ручная)
 * - CRUD операциям с чертежами
 * - Конвертации активных объектов в схемы
 * - Модернизации активных объектов
 */
class KonstructGateway {
  // ============================================================================
  // ГЕНЕРАЦИЯ ЧЕРТЕЖЕЙ
  // ============================================================================
  
  /**
   * Автоматическая генерация чертежа
   * @param {string} type - Тип: 'ship', 'structure', 'vehicle', 'module'
   * @param {Object} params - Параметры генерации
   * @returns {Promise<Object>} Сгенерированный чертеж
   */
  async generateAuto(type, params) {
    try {
      const response = await Gateway.api.scheme.generateAuto(type, params);
      return response;
    } catch (error) {
      console.error(`❌ Failed to auto-generate ${type}:`, error);
      throw error;
    }
  }

  /**
   * Полуавтоматическая генерация с выбором модулей
   * @param {string} type - Тип: 'ship', 'structure', 'vehicle'
   * @param {Object} baseParams - Базовые параметры
   * @param {Array} modules - Список модулей [{purpose: string, count: number}]
   * @returns {Promise<Object>} Сгенерированный чертеж
   */
  async generateSemiAuto(type, baseParams, modules) {
    try {
      const response = await Gateway.api.scheme.generateSemiAuto(type, baseParams, modules);
      return response;
    } catch (error) {
      console.error(`❌ Failed to semi-auto generate ${type}:`, error);
      throw error;
    }
  }

  /**
   * Ручное создание чертежа из модулей
   * @param {string} type - Тип: 'ship', 'structure', 'vehicle'
   * @param {Object} params - Полные параметры чертежа
   * @param {Array} modules - Список модулей
   * @returns {Promise<Object>} Созданный чертеж
   */
  async generateManual(type, params, modules) {
    try {
      const response = await Gateway.api.scheme.generateManual(type, params, modules);
      return response;
    } catch (error) {
      console.error(`❌ Failed to manually generate ${type}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // МОДУЛИ
  // ============================================================================
  
  /**
   * Получить шаблоны модулей
   * @param {Object} params - Параметры фильтрации
   * @param {string} [params.purpose] - Назначение модуля
   * @param {string} [params.category] - Категория
   * @param {string} [params.subcategory] - Подкатегория
   * @param {number} [params.tech_level_min] - Минимальный техуровень
   * @param {number} [params.limit] - Лимит записей
   * @returns {Promise<Array>} Массив шаблонов модулей
   */
  async getModuleTemplates(params = {}) {
    try {
      const response = await Gateway.api.scheme.getModuleTemplates(params);
      return response || [];
    } catch (error) {
      console.error('❌ Failed to get module templates:', error);
      throw error;
    }
  }

  /**
   * Сгенерировать чертеж модуля
   * @param {Object} params - Параметры модуля
   * @param {string} params.purpose - Назначение модуля
   * @returns {Promise<Object>} Сгенерированный модуль
   */
  async generateModuleScheme(params) {
    try {
      const response = await Gateway.api.scheme.generateModuleScheme(params);
      return response;
    } catch (error) {
      console.error('❌ Failed to generate module scheme:', error);
      throw error;
    }
  }

  // ============================================================================
  // ЧЕРТЕЖИ КОРАБЛЕЙ
  // ============================================================================
  
  /**
   * Получить список чертежей кораблей
   * @param {Object} params - Параметры фильтрации и пагинации
   * @returns {Promise<{data: Array, meta: Object}>} Список чертежей и метаинформация
   */
  async listShipSchemes(params = {}) {
    try {
      const response = await Gateway.api.scheme.listShipSchemes(params);
      if (Array.isArray(response)) {
        return { data: response, meta: { total: response.length, page: params.page || 1, limit: params.limit || 20 } };
      }
      return response;
    } catch (error) {
      console.error('❌ Failed to list ship schemes:', error);
      throw error;
    }
  }

  /**
   * Получить чертеж корабля по ID
   * @param {string} id - ID чертежа
   * @returns {Promise<Object>} Чертеж корабля
   */
  async getShipScheme(id) {
    try {
      const response = await Gateway.api.scheme.getShipScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get ship scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Создать чертеж корабля
   * @param {Object} schemeData - Данные чертежа
   * @returns {Promise<Object>} Созданный чертеж
   */
  async createShipScheme(schemeData) {
    try {
      const response = await Gateway.api.scheme.createShipScheme(schemeData);
      return response;
    } catch (error) {
      console.error('❌ Failed to create ship scheme:', error);
      throw error;
    }
  }

  /**
   * Обновить чертеж корабля
   * @param {string} id - ID чертежа
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленный чертеж
   */
  async updateShipScheme(id, updates) {
    try {
      const response = await Gateway.api.scheme.updateShipScheme(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update ship scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить чертеж корабля
   * @param {string} id - ID чертежа
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteShipScheme(id) {
    try {
      const response = await Gateway.api.scheme.deleteShipScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete ship scheme ${id}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // ЧЕРТЕЖИ СТРУКТУР
  // ============================================================================
  
  /**
   * Получить список чертежей структур
   * @param {Object} params - Параметры фильтрации и пагинации
   * @returns {Promise<{data: Array, meta: Object}>} Список чертежей и метаинформация
   */
  async listStructureSchemes(params = {}) {
    try {
      const response = await Gateway.api.scheme.listStructureSchemes(params);
      if (Array.isArray(response)) {
        return { data: response, meta: { total: response.length, page: params.page || 1, limit: params.limit || 20 } };
      }
      return response;
    } catch (error) {
      console.error('❌ Failed to list structure schemes:', error);
      throw error;
    }
  }

  /**
   * Получить чертеж структуры по ID
   * @param {string} id - ID чертежа
   * @returns {Promise<Object>} Чертеж структуры
   */
  async getStructureScheme(id) {
    try {
      const response = await Gateway.api.scheme.getStructureScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get structure scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Создать чертеж структуры
   * @param {Object} schemeData - Данные чертежа
   * @returns {Promise<Object>} Созданный чертеж
   */
  async createStructureScheme(schemeData) {
    try {
      const response = await Gateway.api.scheme.createStructureScheme(schemeData);
      return response;
    } catch (error) {
      console.error('❌ Failed to create structure scheme:', error);
      throw error;
    }
  }

  /**
   * Обновить чертеж структуры
   * @param {string} id - ID чертежа
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленный чертеж
   */
  async updateStructureScheme(id, updates) {
    try {
      const response = await Gateway.api.scheme.updateStructureScheme(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update structure scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить чертеж структуры
   * @param {string} id - ID чертежа
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteStructureScheme(id) {
    try {
      const response = await Gateway.api.scheme.deleteStructureScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete structure scheme ${id}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // 🔥 ЧЕРТЕЖИ СТАНЦИЙ / КОСМОПОРТОВ / ВЕРФЕЙ / ГРАЖД. / ВОЕННЫХ СТРУКТУР
  //    (обёртки для агрегата structure_scheme — их ждёт AssetManager.loadAssetDetails)
  // ============================================================================

  /**
   * Получить чертеж станции по ID
   * @param {string} id - ID чертежа станции
   * @returns {Promise<Object>} Чертеж станции
   */
  async getStationScheme(id) {
    try {
      const response = await Gateway.api.scheme.getStationScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get station scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Получить чертеж космопорта по ID
   * @param {string} id - ID чертежа космопорта
   * @returns {Promise<Object>} Чертеж космопорта
   */
  async getSpaceportScheme(id) {
    try {
      const response = await Gateway.api.scheme.getSpaceportScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get spaceport scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Получить чертеж верфи по ID
   * @param {string} id - ID чертежа верфи
   * @returns {Promise<Object>} Чертеж верфи
   */
  async getShipyardScheme(id) {
    try {
      const response = await Gateway.api.scheme.getShipyardScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get shipyard scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Получить чертеж гражданской структуры по ID
   * @param {string} id - ID чертежа гражданской структуры
   * @returns {Promise<Object>} Чертеж гражданской структуры
   */
  async getCivilStructureScheme(id) {
    try {
      const response = await Gateway.api.scheme.getCivilStructureScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get civil structure scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Получить чертеж военной структуры по ID
   * @param {string} id - ID чертежа военной структуры
   * @returns {Promise<Object>} Чертеж военной структуры
   */
  async getMilitaryStructureScheme(id) {
    try {
      const response = await Gateway.api.scheme.getMilitaryStructureScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get military structure scheme ${id}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // ЧЕРТЕЖИ ТРАНСПОРТА
  // ============================================================================
  
  /**
   * Получить список чертежей транспорта
   * @param {Object} params - Параметры фильтрации и пагинации
   * @returns {Promise<{data: Array, meta: Object}>} Список чертежей и метаинформация
   */
  async listVehicleSchemes(params = {}) {
    try {
      const response = await Gateway.api.scheme.listVehicleSchemes(params);
      if (Array.isArray(response)) {
        return { data: response, meta: { total: response.length, page: params.page || 1, limit: params.limit || 20 } };
      }
      return response;
    } catch (error) {
      console.error('❌ Failed to list vehicle schemes:', error);
      throw error;
    }
  }

  /**
   * Получить чертеж транспорта по ID
   * @param {string} id - ID чертежа
   * @returns {Promise<Object>} Чертеж транспорта
   */
  async getVehicleScheme(id) {
    try {
      const response = await Gateway.api.scheme.getVehicleScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get vehicle scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Создать чертеж транспорта
   * @param {Object} schemeData - Данные чертежа
   * @returns {Promise<Object>} Созданный чертеж
   */
  async createVehicleScheme(schemeData) {
    try {
      const response = await Gateway.api.scheme.createVehicleScheme(schemeData);
      return response;
    } catch (error) {
      console.error('❌ Failed to create vehicle scheme:', error);
      throw error;
    }
  }

  /**
   * Обновить чертеж транспорта
   * @param {string} id - ID чертежа
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленный чертеж
   */
  async updateVehicleScheme(id, updates) {
    try {
      const response = await Gateway.api.scheme.updateVehicleScheme(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update vehicle scheme ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить чертеж транспорта
   * @param {string} id - ID чертежа
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteVehicleScheme(id) {
    try {
      const response = await Gateway.api.scheme.deleteVehicleScheme(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete vehicle scheme ${id}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // АКТИВНЫЕ ОБЪЕКТЫ ДЛЯ МОДЕРНИЗАЦИИ
  // ============================================================================
  
  // === КОРАБЛИ (активные) ===
  
  /**
   * Получить список активных кораблей.
   *
   * 🔥 v2.4-fix: бэкенд `/api/ships` возвращает `{ships: [...], pagination: {...}}`
   * (а не `data: [...]`, как другие endpoint'ы). Клиент `AssetManager.jsx`
   * (`loadAssets`) ждёт **массив** (`Array.isArray(response)`). Эта функция
   * раскрывает `response.data.ships` в массив, чтобы `loadAssets` увидел корабли.
   *
   * @param {Object} params - Параметры фильтрации
   * @returns {Promise<Array>} Список кораблей
   */
  async listActiveShips(params = {}) {
    try {
      const response = await Gateway.api.ship.listShips(params);
      if (response?.status === 'success') {
        const data = response.data;
        // Бэкенд /api/ships: data = { ships: [...], pagination: {...} }
        if (data && Array.isArray(data.ships)) {
          return data.ships;
        }
        // Альтернативный контракт (если бэкенд когда-то вернёт data: [...])
        if (Array.isArray(data)) {
          return data;
        }
        return data ? [data] : [];
      }
      // Fallback: response уже массив (не обёрнут в status)
      if (Array.isArray(response)) {
        return response;
      }
      return response || [];
    } catch (error) {
      console.error('❌ Failed to list active ships:', error);
      throw error;
    }
  }

  /**
   * Получить активный корабль по ID
   * @param {string} id - ID корабля
   * @returns {Promise<Object>} Данные корабля
   */
  async getActiveShip(id) {
    try {
      const response = await Gateway.api.ship.getShip(id);
      return response?.status === 'success' ? response.data : response;
    } catch (error) {
      console.error(`❌ Failed to get active ship ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить корабль
   * @param {string} id - ID корабля
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленный корабль
   */
  async updateShip(id, updates) {
    try {
      const response = await Gateway.api.ship.updateShip(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update ship ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить корабль
   * @param {string} id - ID корабля
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteShip(id) {
    try {
      const response = await Gateway.api.ship.deleteShip(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete ship ${id}:`, error);
      throw error;
    }
  }

  /**
   * Конвертировать активный корабль в схему
   * @param {string} shipId - ID корабля
   * @param {string} schemeName - Название схемы (опционально)
   * @returns {Promise<Object>} Созданная схема
   */
  async convertShipToScheme(shipId, schemeName = null) {
    try {
      const response = await Gateway.api.ship.convertToScheme(shipId, { scheme_name: schemeName });
      return response;
    } catch (error) {
      console.error(`❌ Failed to convert ship ${shipId} to scheme:`, error);
      throw error;
    }
  }

  /**
   * Проверить возможность модернизации корабля
   * @param {string} shipId - ID корабля
   * @returns {Promise<Object>} Информация о возможности модернизации
   */
  async checkShipModernization(shipId) {
    try {
      const response = await Gateway.api.ship.checkModernization(shipId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to check ship modernization ${shipId}:`, error);
      throw error;
    }
  }

  /**
   * Применить модернизацию к кораблю
   * @param {string} shipId - ID корабля
   * @param {Object} modifications - Изменения для применения
   * @param {string} workshopId - ID мастерской (опционально)
   * @returns {Promise<Object>} Обновленный корабль
   */
  async applyShipModernization(shipId, modifications, workshopId = null) {
    try {
      const response = await Gateway.api.ship.applyModernization(shipId, modifications, workshopId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to apply modernization to ship ${shipId}:`, error);
      throw error;
    }
  }

  /**
   * Получить доступные модули для модернизации корабля
   * @param {string} shipId - ID корабля
   * @returns {Promise<Array>} Список доступных модулей
   */
  async getShipAvailableModules(shipId) {
    try {
      const response = await Gateway.api.ship.getAvailableModules(shipId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get available modules for ship ${shipId}:`, error);
      throw error;
    }
  }

  // === КОСМИЧЕСКИЕ СТАНЦИИ (активные) ===
  
  /**
   * Получить список активных космических станций
   * @param {Object} params - Параметры фильтрации
   * @returns {Promise<Array>} Список станций
   */
  async listActiveSpaceStations(params = {}) {
    try {
      const response = await Gateway.api.spaceStation.listStations(params);
      if (response?.status === 'success') {
        return response.data || [];
      }
      return response || [];
    } catch (error) {
      console.error('❌ Failed to list space stations:', error);
      throw error;
    }
  }

  /**
   * Получить активную космическую станцию по ID
   * @param {string} id - ID станции
   * @returns {Promise<Object>} Данные станции
   */
  async getActiveSpaceStation(id) {
    try {
      const response = await Gateway.api.spaceStation.getStation(id);
      return response?.status === 'success' ? response.data : response;
    } catch (error) {
      console.error(`❌ Failed to get space station ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить станцию
   * @param {string} id - ID станции
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленная станция
   */
  async updateStation(id, updates) {
    try {
      const response = await Gateway.api.spaceStation.updateStation(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update station ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить станцию
   * @param {string} id - ID станции
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteStation(id) {
    try {
      const response = await Gateway.api.spaceStation.deleteStation(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete station ${id}:`, error);
      throw error;
    }
  }

  /**
   * Конвертировать станцию в схему
   * @param {string} stationId - ID станции
   * @param {string} schemeName - Название схемы (опционально)
   * @returns {Promise<Object>} Созданная схема
   */
  async convertStationToScheme(stationId, schemeName = null) {
    try {
      const response = await Gateway.api.spaceStation.convertToScheme(stationId, { scheme_name: schemeName });
      return response;
    } catch (error) {
      console.error(`❌ Failed to convert station ${stationId} to scheme:`, error);
      throw error;
    }
  }

  /**
   * Проверить возможность модернизации станции
   * @param {string} stationId - ID станции
   * @returns {Promise<Object>} Информация о возможности модернизации
   */
  async checkStationModernization(stationId) {
    try {
      const response = await Gateway.api.spaceStation.checkModernization(stationId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to check station modernization ${stationId}:`, error);
      throw error;
    }
  }

  /**
   * Применить модернизацию к станции
   * @param {string} stationId - ID станции
   * @param {Object} modifications - Изменения для применения
   * @param {string} workshopId - ID мастерской (опционально)
   * @returns {Promise<Object>} Обновленная станция
   */
  async applyStationModernization(stationId, modifications, workshopId = null) {
    try {
      const response = await Gateway.api.spaceStation.applyModernization(stationId, modifications, workshopId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to apply modernization to station ${stationId}:`, error);
      throw error;
    }
  }

  /**
   * Получить доступные модули для модернизации станции
   * @param {string} stationId - ID станции
   * @returns {Promise<Array>} Список доступных модулей
   */
  async getStationAvailableModules(stationId) {
    try {
      const response = await Gateway.api.spaceStation.getAvailableModules(stationId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get available modules for station ${stationId}:`, error);
      throw error;
    }
  }

  // === КОСМОПОРТЫ (активные) ===
  
  /**
   * Получить список активных космопортов
   * @param {Object} params - Параметры фильтрации
   * @returns {Promise<Array>} Список космопортов
   */
  async listActiveSpaceports(params = {}) {
    try {
      const response = await Gateway.api.spaceport.listSpaceports(params);
      if (response?.status === 'success') {
        return response.data || [];
      }
      return response || [];
    } catch (error) {
      console.error('❌ Failed to list spaceports:', error);
      throw error;
    }
  }

  /**
   * Получить активный космопорт по ID
   * @param {string} id - ID космопорта
   * @returns {Promise<Object>} Данные космопорта
   */
  async getSpaceport(id) {
    try {
      const response = await Gateway.api.spaceport.getSpaceport(id);
      return response?.status === 'success' ? response.data : response;
    } catch (error) {
      console.error(`❌ Failed to get spaceport ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить космопорт
   * @param {string} id - ID космопорта
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленный космопорт
   */
  async updateSpaceport(id, updates) {
    try {
      const response = await Gateway.api.spaceport.updateSpaceport(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update spaceport ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить космопорт
   * @param {string} id - ID космопорта
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteSpaceport(id) {
    try {
      const response = await Gateway.api.spaceport.deleteSpaceport(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete spaceport ${id}:`, error);
      throw error;
    }
  }

  // === ВЕРФИ (активные) ===
  
  /**
   * Получить список активных верфей
   * @param {Object} params - Параметры фильтрации
   * @param {number} params.page - Номер страницы
   * @param {number} params.limit - Лимит записей
   * @param {string} params.owner_id - ID владельца
   * @param {string} params.location - Локация
   * @param {string} params.specialization - Специализация (military, commercial, exploration, universal)
   * @param {string} params.class - Класс верфи (small, medium, large, orbital, drydock)
   * @param {string} params.status - Статус (active, under_construction, etc.)
   * @returns {Promise<{data: Array, meta: Object}>} Список верфей
   */
  async listActiveShipyards(params = {}) {
    try {
      const response = await Gateway.api.shipyard.listShipyards(params);
      if (response?.status === 'success') {
        return { data: response.data || [], meta: response.meta || { total: response.data?.length || 0 } };
      }
      if (Array.isArray(response)) {
        return { data: response, meta: { total: response.length, page: params.page || 1, limit: params.limit || 20 } };
      }
      return { data: response?.shipyards || [], meta: { total: response?.total || 0 } };
    } catch (error) {
      console.error('❌ Failed to list shipyards:', error);
      throw error;
    }
  }

  /**
   * Получить активную верфь по ID
   * @param {string} id - ID верфи
   * @returns {Promise<Object>} Данные верфи
   */
  async getShipyard(id) {
    try {
      const response = await Gateway.api.shipyard.getShipyard(id);
      if (response?.status === 'success') {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error(`❌ Failed to get shipyard ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить верфь
   * @param {string} id - ID верфи
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленная верфь
   */
  async updateShipyard(id, updates) {
    try {
      const response = await Gateway.api.shipyard.updateShipyard(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update shipyard ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить верфь
   * @param {string} id - ID верфи
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteShipyard(id) {
    try {
      const response = await Gateway.api.shipyard.deleteShipyard(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete shipyard ${id}:`, error);
      throw error;
    }
  }

  /**
   * Конвертировать верфь в схему
   * @param {string} shipyardId - ID верфи
   * @param {string} schemeName - Название схемы (опционально)
   * @returns {Promise<Object>} Созданная схема
   */
  async convertShipyardToScheme(shipyardId, schemeName = null) {
    try {
      const response = await Gateway.api.shipyard.convertToScheme(shipyardId, { scheme_name: schemeName });
      return response;
    } catch (error) {
      console.error(`❌ Failed to convert shipyard ${shipyardId} to scheme:`, error);
      throw error;
    }
  }

  /**
   * Проверить возможность модернизации верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Object>} Информация о возможности модернизации
   */
  async checkShipyardModernization(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.checkModernization(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to check shipyard modernization ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Применить модернизацию к верфи
   * @param {string} shipyardId - ID верфи
   * @param {Object} modifications - Изменения для применения
   * @param {string} workshopId - ID мастерской (опционально)
   * @returns {Promise<Object>} Обновленная верфь
   */
  async applyShipyardModernization(shipyardId, modifications, workshopId = null) {
    try {
      const response = await Gateway.api.shipyard.applyModernization(shipyardId, modifications, workshopId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to apply modernization to shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Получить доступные модули для модернизации верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Array>} Список доступных модулей
   */
  async getShipyardAvailableModules(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.getAvailableModules(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get available modules for shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Получить очередь строительства верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Array>} Очередь строительства
   */
  async getShipyardConstructionQueue(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.getConstructionQueue(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get construction queue for shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

   /**
   * Получить очередь строительства верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Array>} Очередь строительства
   */
  async getShipyardConstructionQueue(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.getConstructionQueue(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get construction queue for shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Получить текущие проекты верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Array>} Текущие проекты
   */
  async getShipyardCurrentProjects(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.getCurrentProjects(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get current projects for shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Заказать строительство корабля на верфи
   * @param {string} shipyardId - ID верфи
   * @param {Object} shipDesign - Дизайн корабля
   * @param {Object} options - Опции заказа (priority, budget)
   * @returns {Promise<Object>} Информация о заказе
   */
  async orderShipConstruction(shipyardId, shipDesign, options = {}) {
    try {
      const response = await Gateway.api.shipyard.orderConstruction(shipyardId, shipDesign, options);
      return response;
    } catch (error) {
      console.error(`❌ Failed to order construction at shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Заказать ремонт корабля на верфи
   * @param {string} shipyardId - ID верфи
   * @param {string} shipId - ID корабля
   * @param {string} repairType - Тип ремонта (emergency, standard, overhaul, modernization)
   * @param {Object} options - Опции заказа (priority, services)
   * @returns {Promise<Object>} Информация о заказе
   */
  async orderShipRepair(shipyardId, shipId, repairType, options = {}) {
    try {
      const response = await Gateway.api.shipyard.orderRepair(shipyardId, shipId, repairType, options);
      return response;
    } catch (error) {
      console.error(`❌ Failed to order repair at shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Получить статистику верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Object>} Статистика верфи
   */
  async getShipyardStats(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.getStats(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get stats for shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  /**
   * Получить доступные типы кораблей для верфи
   * @param {string} shipyardId - ID верфи
   * @returns {Promise<Array>} Список доступных типов кораблей
   */
  async getShipyardAvailableShipTypes(shipyardId) {
    try {
      const response = await Gateway.api.shipyard.getAvailableShipTypes(shipyardId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get available ship types for shipyard ${shipyardId}:`, error);
      throw error;
    }
  }

  // === ГРАЖДАНСКИЕ СТРУКТУРЫ (активные) ===
  
  /**
   * Получить список активных гражданских структур
   * @param {Object} params - Параметры фильтрации
   * @returns {Promise<Array>} Список структур
   */
  async listActiveCivilStructures(params = {}) {
    try {
      const response = await Gateway.api.civilStructure.listStructures(params);
      if (response?.status === 'success') {
        return response.data || [];
      }
      return response || [];
    } catch (error) {
      console.error('❌ Failed to list civil structures:', error);
      throw error;
    }
  }

  /**
   * Получить активную гражданскую структуру по ID
   * @param {string} id - ID структуры
   * @returns {Promise<Object>} Данные структуры
   */
  async getStructure(id) {
    try {
      const response = await Gateway.api.civilStructure.getStructure(id);
      return response?.status === 'success' ? response.data : response;
    } catch (error) {
      console.error(`❌ Failed to get structure ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить гражданскую структуру
   * @param {string} id - ID структуры
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленная структура
   */
  async updateStructure(id, updates) {
    try {
      const response = await Gateway.api.civilStructure.updateStructure(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update structure ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить гражданскую структуру
   * @param {string} id - ID структуры
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteStructure(id) {
    try {
      const response = await Gateway.api.civilStructure.deleteStructure(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete structure ${id}:`, error);
      throw error;
    }
  }

  /**
   * Конвертировать гражданскую структуру в схему
   * @param {string} structureId - ID структуры
   * @param {string} schemeName - Название схемы (опционально)
   * @returns {Promise<Object>} Созданная схема
   */
  async convertCivilStructureToScheme(structureId, schemeName = null) {
    try {
      const response = await Gateway.api.civilStructure.convertToScheme(structureId, { scheme_name: schemeName });
      return response;
    } catch (error) {
      console.error(`❌ Failed to convert civil structure ${structureId} to scheme:`, error);
      throw error;
    }
  }

  // === ВОЕННЫЕ СТРУКТУРЫ (активные) ===
  
  /**
   * Получить список активных военных структур
   * @param {Object} params - Параметры фильтрации
   * @returns {Promise<Array>} Список структур
   */
  async listActiveMilitaryStructures(params = {}) {
    try {
      const response = await Gateway.api.militaryStructure.listMilitaryStructures(params);
      if (response?.status === 'success') {
        return response.data || [];
      }
      return response || [];
    } catch (error) {
      console.error('❌ Failed to list military structures:', error);
      throw error;
    }
  }

  /**
   * Получить активную военную структуру по ID
   * @param {string} id - ID структуры
   * @returns {Promise<Object>} Данные структуры
   */
  async getMilitaryStructure(id) {
    try {
      const response = await Gateway.api.militaryStructure.getMilitaryStructure(id);
      return response?.status === 'success' ? response.data : response;
    } catch (error) {
      console.error(`❌ Failed to get military structure ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить военную структуру
   * @param {string} id - ID структуры
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленная структура
   */
  async updateMilitaryStructure(id, updates) {
    try {
      const response = await Gateway.api.militaryStructure.updateMilitaryStructure(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update military structure ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить военную структуру
   * @param {string} id - ID структуры
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteMilitaryStructure(id) {
    try {
      const response = await Gateway.api.militaryStructure.deleteMilitaryStructure(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete military structure ${id}:`, error);
      throw error;
    }
  }

  /**
   * Конвертировать военную структуру в схему
   * @param {string} structureId - ID структуры
   * @param {string} schemeName - Название схемы (опционально)
   * @returns {Promise<Object>} Созданная схема
   */
  async convertMilitaryStructureToScheme(structureId, schemeName = null) {
    try {
      const response = await Gateway.api.militaryStructure.convertToScheme(structureId, { scheme_name: schemeName });
      return response;
    } catch (error) {
      console.error(`❌ Failed to convert military structure ${structureId} to scheme:`, error);
      throw error;
    }
  }

  // === ТРАНСПОРТ (активный) ===
  
  /**
   * Получить список активного транспорта
   * @param {Object} params - Параметры фильтрации
   * @returns {Promise<Array>} Список транспорта
   */
  async listActiveVehicles(params = {}) {
    try {
      const response = await Gateway.api.vehicle.listVehicles(params);
      if (response?.status === 'success') {
        return response.data || [];
      }
      return response || [];
    } catch (error) {
      console.error('❌ Failed to list vehicles:', error);
      throw error;
    }
  }

  /**
   * Получить активный транспорт по ID
   * @param {string} id - ID транспорта
   * @returns {Promise<Object>} Данные транспорта
   */
  async getVehicle(id) {
    try {
      const response = await Gateway.api.vehicle.getVehicle(id);
      return response?.status === 'success' ? response.data : response;
    } catch (error) {
      console.error(`❌ Failed to get vehicle ${id}:`, error);
      throw error;
    }
  }

  /**
   * Обновить транспорт
   * @param {string} id - ID транспорта
   * @param {Object} updates - Обновляемые поля
   * @returns {Promise<Object>} Обновленный транспорт
   */
  async updateVehicle(id, updates) {
    try {
      const response = await Gateway.api.vehicle.updateVehicle(id, updates);
      return response;
    } catch (error) {
      console.error(`❌ Failed to update vehicle ${id}:`, error);
      throw error;
    }
  }

  /**
   * Удалить транспорт
   * @param {string} id - ID транспорта
   * @returns {Promise<Object>} Результат удаления
   */
  async deleteVehicle(id) {
    try {
      const response = await Gateway.api.vehicle.deleteVehicle(id);
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete vehicle ${id}:`, error);
      throw error;
    }
  }

  /**
   * Конвертировать транспорт в схему
   * @param {string} vehicleId - ID транспорта
   * @param {string} schemeName - Название схемы (опционально)
   * @returns {Promise<Object>} Созданная схема
   */
  async convertVehicleToScheme(vehicleId, schemeName = null) {
    try {
      const response = await Gateway.api.vehicle.convertToScheme(vehicleId, { scheme_name: schemeName });
      return response;
    } catch (error) {
      console.error(`❌ Failed to convert vehicle ${vehicleId} to scheme:`, error);
      throw error;
    }
  }

  // ============================================================================
  // МАСТЕРСКИЕ
  // ============================================================================
  
  /**
   * Получить мастерские в локации
   * @param {string} location - Локация
   * @returns {Promise<Array>} Список мастерских
   */
  async getWorkshops(location) {
    try {
      const response = await Gateway.api.vehicle.getWorkshops(location);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get workshops in ${location}:`, error);
      throw error;
    }
  }

  /**
   * Получить доступные услуги мастерской
   * @param {string} workshopId - ID мастерской
   * @returns {Promise<Array>} Список услуг
   */
  async getWorkshopServices(workshopId) {
    try {
      const response = await Gateway.api.vehicle.getWorkshopServices(workshopId);
      return response;
    } catch (error) {
      console.error(`❌ Failed to get services for workshop ${workshopId}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
  // ============================================================================
  
  /**
   * Получить статистику чертежа
   * @param {Object} scheme - Чертеж
   * @returns {Object} Статистика
   */
  getSchemeStats(scheme) {
    return Gateway.utils.schemes.calculateTotalStats ? 
      Gateway.utils.schemes.calculateTotalStats(scheme) : 
      Gateway.api.scheme.calculateSchemeStats(scheme);
  }

  /**
   * Получить иконку для типа чертежа
   * @param {string} type - Тип
   * @returns {string} Иконка
   */
  getIconForSchemeType(type) {
    return Gateway.utils.schemes.getIconForSchemeType(type);
  }

  /**
   * Получить статусную метку
   * @param {string} status - Статус
   * @returns {string} Метка
   */
  getStatusLabel(status) {
    return Gateway.utils.schemes.getStatusLabel(status);
  }
}

export default new KonstructGateway();